import React, { useState } from 'react';
import { Mail, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { submitDirectMessage, checkDMRateLimit } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';

/**
 * ContactRenderer - Renders the contact form block
 * Themed contact form with rate limiting
 */
// Get or create visitor ID for rate limiting (matches legacy ContactFormSection behavior)
const getVisitorId = () => {
  let visitorId = localStorage.getItem('chessweb_visitor_id');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chessweb_visitor_id', visitorId);
  }
  return visitorId;
};

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const ContactRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading = 'Get in Touch',
    description = '',
    showNameField = true,
    showSubscribeOption = true,
  } = content;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    subscribe: false,
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error, rate-limited
  const [errorMessage, setErrorMessage] = useState('');

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (status === 'error' || status === 'rate-limited') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.email?.trim()) {
      setStatus('error');
      setErrorMessage('Please enter your email address.');
      return;
    }
    if (!formData.message?.trim()) {
      setStatus('error');
      setErrorMessage('Please enter a message.');
      return;
    }

    setStatus('loading');

    try {
      // Check rate limit using visitorId (matches legacy ContactFormSection behavior)
      const visitorId = getVisitorId();
      const canSend = await checkDMRateLimit(profileId, visitorId);
      if (!canSend) {
        setStatus('rate-limited');
        setErrorMessage('You have reached the message limit. Please try again in an hour.');
        return;
      }

      // Submit message
      await submitDirectMessage(
        profileId,
        formData.email,
        formData.name || '',
        formData.message,
        formData.subscribe
      );

      setStatus('success');
      setFormData({ name: '', email: '', message: '', subscribe: false });
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus('error');
      setErrorMessage('Failed to send message. Please try again.');
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-lg mx-auto text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${styles.accentColor}20` }}
          >
            <CheckCircle size={32} style={{ color: styles.accentColor }} />
          </div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            Message Sent!
          </h3>
          <p style={{ color: styles.mutedColor }} className="mb-6">
            Thanks for reaching out. I'll get back to you as soon as possible.
          </p>

        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {heading && (
            <h2
              className="text-2xl sm:text-3xl font-bold mb-3"
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}
          {description && (
            <p className="whitespace-pre-wrap" style={{ color: styles.mutedColor }}>{description}</p>
          )}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-6 sm:p-8"
          style={{ backgroundColor: styles.cardBackground }}
        >
          {/* Error/Rate Limit Message */}
          {(status === 'error' || status === 'rate-limited') && (
            <div
              className="mb-6 p-4 rounded-lg flex items-start gap-3"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
            >
              <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{errorMessage}</p>
            </div>
          )}

          {/* Name Field */}
          {showNameField && (
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: styles.bodyColor }}
              >
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none"
                style={{
                  backgroundColor: styles.inputBackground,
                  borderColor: styles.borderColor,
                  color: styles.headingColor,
                }}
                onFocus={(e) => (e.target.style.borderColor = styles.accentColor)}
                onBlur={(e) => (e.target.style.borderColor = styles.borderColor)}
              />
            </div>
          )}

          {/* Email Field */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: styles.bodyColor }}
            >
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your@email.com"
              required
              className="w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none"
              style={{
                backgroundColor: styles.inputBackground,
                borderColor: styles.borderColor,
                color: styles.headingColor,
              }}
              onFocus={(e) => (e.target.style.borderColor = styles.accentColor)}
              onBlur={(e) => (e.target.style.borderColor = styles.borderColor)}
            />
          </div>

          {/* Message Field */}
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: styles.bodyColor }}
            >
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Your message..."
              required
              rows={5}
              className="w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none resize-none"
              style={{
                backgroundColor: styles.inputBackground,
                borderColor: styles.borderColor,
                color: styles.headingColor,
              }}
              onFocus={(e) => (e.target.style.borderColor = styles.accentColor)}
              onBlur={(e) => (e.target.style.borderColor = styles.borderColor)}
            />
          </div>

          {/* Subscribe Checkbox */}
          {showSubscribeOption && (
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.subscribe}
                  onChange={(e) => handleInputChange('subscribe', e.target.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: styles.accentColor }}
                />
                <span className="text-sm" style={{ color: styles.mutedColor }}>
                  Keep me updated with news and announcements
                </span>
              </label>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
            style={styles.getButtonStyles()}
          >
            {status === 'loading' ? (
              <>
                <Loader size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Message
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactRenderer;
